import { Stack } from "@mui/material";
import { IconButton } from "components/IconButton";
import { Slider } from "components/Slider";
import { useEffect, useRef, useState } from "react";

type EnergyAnalysisSnapshotYearSliderProps = {
  disabled?: boolean;
  onChange: (year: number) => void;
  year?: number;
  years: number[];
};

const PLAYBACK_INTERVAL = 200;

export const EnergyAnalysisSnapshotYearSlider = ({
  disabled,
  onChange,
  year,
  years,
}: EnergyAnalysisSnapshotYearSliderProps) => {
  const selectedIndex = Math.max(0, years.indexOf(year ?? years[years.length - 1] ?? 0));
  const lastIndex = Math.max(0, years.length - 1);
  const playbackIndex = useRef(selectedIndex);
  const onChangeRef = useRef(onChange);
  const yearsRef = useRef(years);
  const [isPlaying, setIsPlaying] = useState(false);

  onChangeRef.current = onChange;
  yearsRef.current = years;

  useEffect(() => {
    playbackIndex.current = selectedIndex;
  }, [selectedIndex]);

  useEffect(() => {
    if (!isPlaying || disabled || lastIndex < 1) return;

    const interval = window.setInterval(() => {
      const nextIndex = playbackIndex.current + 1;

      if (nextIndex > lastIndex) {
        setIsPlaying(false);
        return;
      }

      playbackIndex.current = nextIndex;
      onChangeRef.current(yearsRef.current[nextIndex]);

      if (nextIndex === lastIndex) setIsPlaying(false);
    }, PLAYBACK_INTERVAL);

    return () => window.clearInterval(interval);
  }, [disabled, isPlaying, lastIndex]);

  const togglePlayback = () => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }

    if (selectedIndex === lastIndex) {
      playbackIndex.current = 0;
      onChange(years[0]);
    }

    setIsPlaying(true);
  };

  const isPlaybackDisabled = disabled || years.length < 2;

  return (
    <Stack className="gap-4">
      <IconButton
        buttonProps={{
          disabled: isPlaybackDisabled,
          onClick: togglePlayback,
          size: "sm",
          variant: "tertiary",
        }}
        iconProps={{
          iconName: isPlaying ? "Pause" : "Play",
          size: "md",
        }}
      />
      <Stack className="w-64 items-start gap-3">
        <Slider
          disabled={isPlaybackDisabled}
          marks={
            years.length > 1
              ? [
                  { label: `${years[0]}`, value: 0 },
                  { label: `${years[lastIndex]}`, value: lastIndex },
                ]
              : undefined
          }
          max={lastIndex}
          min={0}
          onChange={(_, value) => {
            const nextYear = years[Array.isArray(value) ? value[0] : value];
            if (nextYear !== undefined) {
              setIsPlaying(false);
              onChange(nextYear);
            }
          }}
          step={1}
          value={selectedIndex}
          valueLabelDisplay="auto"
          valueLabelFormat={(index) => `${years[index] ?? ""}`}
        />
        <span className="min-w-10 self-end text-right text-lds-foreground-heading-active font-ds-label-md">
          {years[selectedIndex] ?? "—"}
        </span>
      </Stack>
    </Stack>
  );
};
